import { Archive, Check, X } from "@untitledui/icons";
import { useCallback, useState } from "react";
import { useApproveSampleRequest, useDenySampleRequest } from "@/api/collaborations";
import { useIgnoreSampleRequest } from "@/api/sample-requests";
import { Button } from "@/components/base/buttons/button";
import { CloseButton } from "@/components/base/buttons/close-button";
import { error, success, warning } from "@/providers/ToastProvider";
import { type SelectedSample, useSampleSelection } from "./sample-selection-context";

type BulkAction = "approve" | "reject" | "ignore";

const PAST_TENSE: Record<BulkAction, string> = {
  approve: "approved",
  reject: "rejected",
  ignore: "ignored",
};

type WithCollab = SelectedSample & { collaborationId: string };
const hasCollab = (i: SelectedSample): i is WithCollab => i.collaborationId !== null;

/** Run `task` over `items` with bounded concurrency. */
async function runInBatches<T>(items: T[], size: number, task: (item: T) => Promise<unknown>) {
  const results: PromiseSettledResult<unknown>[] = [];
  for (let i = 0; i < items.length; i += size) {
    const batch = items.slice(i, i + size);
    results.push(...(await Promise.allSettled(batch.map(task))));
  }
  return results;
}

function useBulkSampleAction() {
  const approve = useApproveSampleRequest();
  const deny = useDenySampleRequest();
  const ignore = useIgnoreSampleRequest();
  const { clear } = useSampleSelection();
  const [isPending, setIsPending] = useState(false);

  const run = useCallback(
    async (action: BulkAction, items: SelectedSample[]) => {
      // approve/reject require a collaboration; ignore does not
      const eligible = action === "ignore" ? items : items.filter(hasCollab);
      const skipped = items.length - eligible.length;

      if (eligible.length === 0) {
        error(`Selected requests have no collaboration — cannot ${action}.`);
        return;
      }

      setIsPending(true);
      try {
        const results = await runInBatches(eligible, 4, (i) => {
          if (action === "ignore") return ignore.mutateAsync(i.applicationId);
          // narrowed by hasCollab filter above
          const collab = (i as WithCollab).collaborationId;
          return action === "approve"
            ? approve.mutateAsync({ collaborationId: collab, applicationId: i.applicationId })
            : deny.mutateAsync({ collaborationId: collab, applicationId: i.applicationId, reason: "OTHER" });
        });

        const failed = results.filter((r) => r.status === "rejected").length;
        const succeeded = results.length - failed;
        const verb = PAST_TENSE[action];

        if (failed === 0 && skipped === 0) {
          success(`${succeeded} ${succeeded === 1 ? "request" : "requests"} ${verb}`);
        } else if (succeeded === 0) {
          error(`Failed to ${action} ${failed} ${failed === 1 ? "request" : "requests"}`);
        } else {
          const parts = [`${succeeded} ${verb}`];
          if (failed > 0) parts.push(`${failed} failed`);
          if (skipped > 0) parts.push(`${skipped} skipped (no collaboration)`);
          warning(parts.join(", "));
        }
      } finally {
        setIsPending(false);
        clear();
      }
    },
    [approve, deny, ignore, clear],
  );

  return { run, isPending };
}

export function SampleBulkActionBar() {
  const { selected, count, clear } = useSampleSelection();
  const { run, isPending } = useBulkSampleAction();

  if (count === 0) return null;

  const items = Array.from(selected.values());

  return (
    <div
      role="toolbar"
      aria-label="Bulk actions"
      className="-my-2 flex flex-row items-center justify-between rounded-lg bg-brand-primary p-2"
    >
      <div className="flex flex-row items-center gap-2">
        <CloseButton onClick={clear} />
        <span className="text-sm font-medium">{count.toLocaleString()} selected</span>
      </div>
      <div className="flex flex-row items-center gap-2">
        <Button
          color="secondary"
          size="sm"
          iconLeading={Archive}
          isDisabled={isPending}
          onClick={() => run("ignore", items)}
        >
          Ignore
        </Button>
        <Button
          color="secondary"
          size="sm"
          iconLeading={X}
          className="text-error-primary"
          isDisabled={isPending}
          onClick={() => run("reject", items)}
        >
          Reject
        </Button>
        <Button
          color="secondary"
          size="sm"
          iconLeading={Check}
          className="text-success-primary"
          isDisabled={isPending}
          onClick={() => run("approve", items)}
        >
          Approve
        </Button>
      </div>
    </div>
  );
}
